import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    useTheme,
    Divider,
    Card,
    CardContent,
    Button,
    Container,
    Chip,
    Avatar,
    IconButton,
    Tooltip,
    Stack,
    alpha,
    CardHeader,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton
} from '@mui/material';
import {
    ShoppingCart,
    AttachMoney,
    People,
    CheckCircle,
    ArrowForward,
    PlayCircle,
    Speed,
    Refresh,
    Analytics,
    Timeline,
    Restaurant,
    Bed,
    CleaningServices,
    Event,
    House,
    Nightlife as NightlifeIcon,
    Inventory,
    Settings,
    Groups,
    AccountBalanceWallet,
    Assessment,
    PointOfSale,
    Fastfood,
    TableBar,
    Spa,
    FitnessCenter,
    MeetingRoom,
    HowToReg as CheckInIcon,
    ExitToApp as CheckOutIcon,
    AccountCircle,
    Room,
    RoomService,
    Image,
    EventSeat,
    Summarize,
    Checklist,
    DryCleaning,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getUserRole } from '../utils/Permission';
import { companyInfo } from '../utils/dataInfo';

// Helper function to get current user type from session storage
const getCurrentUserType = () => {
    try {
        const userInfo = sessionStorage.getItem('userInfo');
        if (userInfo) {
            const user = JSON.parse(userInfo);
            return user.user_type || 'other';
        }
    } catch (error) {
        console.error('Error getting user type:', error);
    }
    return 'other'; // Default to 'other'
};

const Dashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [userRole, setUserRole] = useState('');
    const [userType, setUserType] = useState('');

    useEffect(() => {
        const role = getUserRole();
        const type = getCurrentUserType();
        setUserRole(role);
        setUserType(type);
        
        // Debug logging
        console.log('Current User Type:', type);
        console.log('User Role:', role);
    }, []);

    // Define module visibility based on user type
    const isModuleVisible = (moduleKey) => {
        const visibilityMap = {
            // Club user modules
            club: {
                roomReservation: false,
                restaurant: false,
                houseKeeping: false,
                eventManagement: false,
                guestHouse: false,
                club: true,
                inventory: true,
                humanResources: true,
                accounting: true,
                systemSettings: true,
                coreOperations: false,
                guestServices: false,
                managementReports: true
            },
            // Other user modules
            other: {
                roomReservation: true,
                restaurant: true,
                houseKeeping: true,
                eventManagement: true,
                guestHouse: true,
                club: false,
                inventory: true,
                humanResources: true,
                accounting: true,
                systemSettings: true,
                coreOperations: true,
                guestServices: true,
                managementReports: true
            }
        };
        
        return visibilityMap[userType]?.[moduleKey] || false;
    };

    // Main modules configuration with visibility flags
    const getAllModules = () => {
        const allModules = [
            {
                key: 'roomReservation',
                title: "Room Reservation",
                icon: <Bed sx={{ fontSize: 30 }} />,
                description: "Manage bookings, check-ins, check-outs, and room status",
                path: "/reservation/booking",
                color: theme.palette.primary.main,
                stats: [
                    { label: "Bookings", value: "Manage", icon: <Bed /> },
                    { label: "Check-in", value: "Process", icon: <CheckInIcon /> },
                    { label: "Check-out", value: "Handle", icon: <CheckOutIcon /> }
                ]
            },
            {
                key: 'restaurant',
                title: "Restaurant Management",
                icon: <Restaurant sx={{ fontSize: 30 }} />,
                description: "POS, orders, menu management, and kitchen operations",
                path: "/restaurant/pos/pos",
                color: theme.palette.secondary.main,
                stats: [
                    { label: "POS", value: "Billing", icon: <PointOfSale /> },
                    { label: "Orders", value: "Track", icon: <ShoppingCart /> },
                    { label: "Menu", value: "Manage", icon: <Fastfood /> }
                ]
            },
            {
                key: 'houseKeeping',
                title: "House Keeping",
                icon: <CleaningServices sx={{ fontSize: 30 }} />,
                description: "Room cleaning, laundry services, and maintenance",
                path: "/house-keeping/room-cleaning/cleaning",
                color: theme.palette.success.main,
                stats: [
                    { label: "Cleaning", value: "Schedule", icon: <CleaningServices /> },
                    { label: "Laundry", value: "Manage", icon: <DryCleaning /> },
                    { label: "Checklists", value: "Create", icon: <Checklist /> }
                ]
            },
            {
                key: 'eventManagement',
                title: "Event Management",
                icon: <Event sx={{ fontSize: 30 }} />,
                description: "Venue booking, decorations, and event coordination",
                path: "/event-management/venue",
                color: theme.palette.warning.main,
                stats: [
                    { label: "Venues", value: "Book", icon: <MeetingRoom /> },
                    { label: "Decorations", value: "Arrange", icon: <Image /> },
                    { label: "Equipment", value: "Rent", icon: <EventSeat /> }
                ]
            },
            {
                key: 'guestHouse',
                title: "Guest House",
                icon: <House sx={{ fontSize: 30 }} />,
                description: "Guest registration, facilities (sauna, gym), and services",
                path: "/guest-house/guest-house",
                color: theme.palette.info.main,
                stats: [
                    { label: "Guests", value: "Register", icon: <People /> },
                    { label: "Sauna", value: "Manage", icon: <Spa /> },
                    { label: "Gym", value: "Facility", icon: <FitnessCenter /> }
                ]
            },
            {
                key: 'club',
                title: "Club Management",
                icon: <NightlifeIcon sx={{ fontSize: 30 }} />,
                description: "Counter operations, inventory, and club services",
                path: "/club/common/counter",
                color: theme.palette.error.main,
                stats: [
                    { label: "Counters", value: "Operate", icon: <TableBar /> },
                    { label: "Inventory", value: "Manage", icon: <Inventory /> },
                    { label: "Reports", value: "Generate", icon: <Assessment /> }
                ]
            }
        ];
        
        // Filter modules based on user type
        return allModules.filter(module => isModuleVisible(module.key));
    };

    // Quick access cards with visibility
    const getQuickAccess = () => {
        const allQuickAccess = [
            {
                key: 'inventory',
                title: "Inventory Management",
                icon: <Inventory sx={{ fontSize: 40 }} />,
                color: "primary",
                items: ["Products", "Categories", "Suppliers", "Units"],
                path: "/units/products"
            },
            {
                key: 'humanResources',
                title: "Human Resources",
                icon: <Groups sx={{ fontSize: 40 }} />,
                color: "secondary",
                items: ["Employees", "Attendance", "Salary", "Departments"],
                path: "/hr-employee/employee"
            },
            {
                key: 'accounting',
                title: "Accounting",
                icon: <AccountBalanceWallet sx={{ fontSize: 40 }} />,
                color: "success",
                items: ["Vouchers", "Reports", "Financial Year", "Chart of Accounts"],
                path: "/account/vouchers/debit"
            },
            {
                key: 'systemSettings',
                title: "System Settings",
                icon: <Settings sx={{ fontSize: 40 }} />,
                color: "warning",
                items: ["Users", "Branches", "Configuration", "Permissions"],
                path: "/settings/users"
            }
        ];
        
        return allQuickAccess.filter(access => isModuleVisible(access.key));
    };

    // Getting started steps (always visible for all users)
    const gettingStarted = [
        {
            title: "Set Up Your Profile",
            description: "Configure your account settings and preferences",
            icon: <AccountCircle />,
            action: "Go to Profile",
            path: "/settings/profile"
        },
        {
            title: "Manage Your Modules",
            description: "Access the modules available for your user type",
            icon: <Settings />,
            action: "View Modules",
            path: "/dashboard"
        }
    ];

    // System modules overview with visibility
    const getSystemModules = () => {
        const allSystemModules = [
            {
                category: "Core Operations",
                key: 'coreOperations',
                modules: [
                    { name: "Room Reservation", path: "/reservation", icon: <Bed />, moduleKey: 'roomReservation' },
                    { name: "Restaurant POS", path: "/restaurant", icon: <Restaurant />, moduleKey: 'restaurant' },
                    { name: "Housekeeping", path: "/house-keeping", icon: <CleaningServices />, moduleKey: 'houseKeeping' },
                    { name: "Inventory", path: "/units", icon: <Inventory />, moduleKey: 'inventory' }
                ]
            },
            {
                category: "Guest Services",
                key: 'guestServices',
                modules: [
                    { name: "Guest House", path: "/guest-house", icon: <House />, moduleKey: 'guestHouse' },
                    { name: "Club", path: "/club", icon: <NightlifeIcon />, moduleKey: 'club' },
                    { name: "Event Management", path: "/event-management", icon: <Event />, moduleKey: 'eventManagement' },
                    { name: "Room Facilities", path: "/room-facility", icon: <RoomService />, moduleKey: 'roomReservation' }
                ]
            },
            {
                category: "Management & Reports",
                key: 'managementReports',
                modules: [
                    { name: "Human Resources", path: "/hr", icon: <Groups />, moduleKey: 'humanResources' },
                    { name: "Accounting", path: "/account", icon: <AccountBalanceWallet />, moduleKey: 'accounting' },
                    { name: "Reports", path: "/reports", icon: <Assessment />, moduleKey: 'managementReports' },
                    { name: "Day Closing", path: "/day-closing", icon: <Summarize />, moduleKey: 'managementReports' }
                ]
            }
        ];
        
        // Filter categories and their modules
        return allSystemModules
            .filter(category => isModuleVisible(category.key))
            .map(category => ({
                ...category,
                modules: category.modules.filter(module => isModuleVisible(module.moduleKey))
            }))
            .filter(category => category.modules.length > 0);
    };

    // Feature categories with visibility
    const getFeatureCategories = () => {
        const allFeatureCategories = [
            {
                key: 'roomReservation',
                title: "Room Management",
                icon: <Room />,
                features: [
                    "Room types and rates",
                    "Booking management",
                    "Check-in/Check-out",
                    "Room status tracking",
                    "Housekeeping schedule"
                ]
            },
            {
                key: 'restaurant',
                title: "Restaurant & Bar",
                icon: <Restaurant />,
                features: [
                    "POS billing",
                    "Order management",
                    "Menu customization",
                    "Table reservations",
                    "Kitchen display"
                ]
            },
            {
                key: 'inventory',
                title: "Inventory Control",
                icon: <Inventory />,
                features: [
                    "Stock management",
                    "Purchase orders",
                    "Supplier management",
                    "Low stock alerts",
                    "Inventory reports"
                ]
            },
            {
                key: 'accounting',
                title: "Accounting",
                icon: <AttachMoney />,
                features: [
                    "Voucher management",
                    "Financial reports",
                    "Expense tracking",
                    "Tax calculations",
                    "Profit/loss statements"
                ]
            }
        ];
        
        return allFeatureCategories.filter(category => isModuleVisible(category.key));
    };

    const mainModules = getAllModules();
    const quickAccess = getQuickAccess();
    const systemModules = getSystemModules();
    const featureCategories = getFeatureCategories();

    // Get user type display name
    const getUserTypeDisplay = () => {
        return userType === 'club' ? 'Club User' : 'Hotel Administrator';
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header Section */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ mb: 4 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography variant="h6" sx={{
                                fontWeight: 500, 
                                color: theme.palette.primary.main,
                                mb: 1
                            }}>
                                {companyInfo.name} Management System
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                                Comprehensive solution for managing all hotel operations in one platform
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Chip 
                                icon={<Speed />} 
                                label={`Role: ${userRole || 'User'}`} 
                                color="primary" 
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                            />
                            <Chip 
                                icon={<People />} 
                                label={`User Type: ${getUserTypeDisplay()}`} 
                                color="secondary" 
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                            />
                            <Tooltip title="Refresh Dashboard">
                                <IconButton 
                                    sx={{ 
                                        bgcolor: theme.palette.primary.main,
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.dark
                                        }
                                    }}
                                    onClick={() => window.location.reload()}
                                >
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    <Divider sx={{ my: 3 }} />
                </Box>
            </motion.div>

            {/* Welcome Section with User Type Specific Message */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
            >
                <Card sx={{ 
                    mb: 4, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white'
                }}>
                    <CardContent sx={{ p: 4 }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                    Welcome to Your {userType === 'club' ? 'Club' : 'Hotel'} Management Dashboard
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                                    {userType === 'club' 
                                        ? "This dashboard is tailored for club operations. You can manage counters, inventory, and club services efficiently from here."
                                        : "This comprehensive system helps you manage all aspects of your hotel operations including room reservations, restaurant management, inventory, accounting, and more."
                                    }
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Main Modules Grid - Only shows modules accessible to current user */}
            {mainModules.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: theme.palette.primary.dark }}>
                        {userType === 'club' ? 'Club Management Modules' : 'Core System Modules'}
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {mainModules.map((module, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <Card 
                                    elevation={2}
                                    sx={{ 
                                        height: '100%',
                                        borderRadius: 3,
                                        borderLeft: `4px solid ${module.color}`,
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[6],
                                            borderLeft: `4px solid ${alpha(module.color, 0.8)}`
                                        }
                                    }}
                                    onClick={() => navigate(module.path)}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                            <Box sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: alpha(module.color, 0.1),
                                                color: module.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                {module.icon}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {module.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {module.description}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        
                                        <Divider sx={{ my: 2 }} />
                                        
                                        <Grid container spacing={1}>
                                            {module.stats.map((stat, idx) => (
                                                <Grid item xs={4} key={idx}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 700, color: module.color }}>
                                                            {stat.value}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {stat.label}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        
                                        <Button 
                                            fullWidth
                                            endIcon={<ArrowForward />}
                                            sx={{ 
                                                mt: 2,
                                                bgcolor: alpha(module.color, 0.1),
                                                color: module.color,
                                                '&:hover': {
                                                    bgcolor: alpha(module.color, 0.2)
                                                }
                                            }}
                                        >
                                            Access Module
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            )}

            {/* Quick Access Section */}
            {quickAccess.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: theme.palette.primary.dark }}>
                        Quick Access
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {quickAccess.map((access, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card 
                                    elevation={2}
                                    sx={{ 
                                        height: '100%',
                                        borderRadius: 3,
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: theme.shadows[8]
                                        }
                                    }}
                                    onClick={() => navigate(access.path)}
                                >
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <Box sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette[access.color]?.main || theme.palette.primary.main, 0.1),
                                            color: theme.palette[access.color]?.main || theme.palette.primary.main,
                                            display: 'inline-flex',
                                            mb: 2
                                        }}>
                                            {access.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {access.title}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            {access.items.map((item, idx) => (
                                                <Chip 
                                                    key={idx}
                                                    label={item}
                                                    size="small"
                                                    sx={{ 
                                                        m: 0.5,
                                                        bgcolor: alpha(theme.palette[access.color]?.main || theme.palette.primary.main, 0.1),
                                                        color: theme.palette[access.color]?.main || theme.palette.primary.main
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            )}

            {/* System Features */}
            {featureCategories.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                                <Analytics sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        System Features & Capabilities
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Everything you need to manage your {userType === 'club' ? 'club' : 'hotel'} efficiently
                                    </Typography>
                                </Box>
                            </Stack>
                            
                            <Grid container spacing={3}>
                                {featureCategories.map((category, index) => (
                                    <Grid item xs={12} md={6} lg={3} key={index}>
                                        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                                            <CardContent>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    mb: 2,
                                                    color: theme.palette.primary.main 
                                                }}>
                                                    {category.icon}
                                                    <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
                                                        {category.title}
                                                    </Typography>
                                                </Box>
                                                <List dense>
                                                    {category.features.map((feature, idx) => (
                                                        <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                                                            <ListItemIcon sx={{ minWidth: 30 }}>
                                                                <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                                                            </ListItemIcon>
                                                            <ListItemText 
                                                                primary={feature}
                                                                primaryTypographyProps={{ 
                                                                    variant: 'body2',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* Getting Started & System Overview */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
            >
                <Grid container spacing={3}>
                    {/* Getting Started */}
                    <Grid item xs={12} lg={6}>
                        <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                            <CardHeader 
                                title="Getting Started Guide"
                                avatar={
                                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                        <PlayCircle />
                                    </Avatar>
                                }
                                subheader="Complete these steps to optimize your system usage"
                            />
                            <CardContent sx={{ p: 0 }}>
                                <List>
                                    {gettingStarted.map((step, index) => (
                                        <ListItem 
                                            key={index}
                                            disablePadding
                                            sx={{ 
                                                borderBottom: index < gettingStarted.length - 1 ? '1px solid' : 'none',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <ListItemButton 
                                                onClick={() => navigate(step.path)}
                                                sx={{ py: 2, px: 3 }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 45 }}>
                                                    <Avatar sx={{ 
                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        color: theme.palette.primary.main,
                                                        width: 36,
                                                        height: 36
                                                    }}>
                                                        {step.icon}
                                                    </Avatar>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                            {step.title}
                                                        </Typography>
                                                    }
                                                    secondary={step.description}
                                                />
                                                <Button 
                                                    size="small" 
                                                    endIcon={<ArrowForward />}
                                                    sx={{ ml: 2 }}
                                                >
                                                    {step.action}
                                                </Button>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* System Overview */}
                    {systemModules.length > 0 && (
                        <Grid item xs={12} lg={6}>
                            <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                                <CardHeader 
                                    title="System Modules Overview"
                                    avatar={
                                        <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                                            <Timeline />
                                        </Avatar>
                                    }
                                    subheader="Access all system modules from here"
                                />
                                <CardContent sx={{ p: 0 }}>
                                    <List>
                                        {systemModules.map((category, catIndex) => (
                                            <React.Fragment key={catIndex}>
                                                <ListItem sx={{ py: 1, px: 3, bgcolor: 'action.hover' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                                        {category.category}
                                                    </Typography>
                                                </ListItem>
                                                {category.modules.map((module, modIndex) => (
                                                    <ListItem 
                                                        key={modIndex}
                                                        sx={{ 
                                                            py: 1.5,
                                                            px: 3,
                                                            borderBottom: (catIndex < systemModules.length - 1 || modIndex < category.modules.length - 1) ? '1px solid' : 'none',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        <ListItemButton 
                                                            onClick={() => navigate(module.path)}
                                                            sx={{ borderRadius: 1 }}
                                                        >
                                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                                {module.icon}
                                                            </ListItemIcon>
                                                            <ListItemText 
                                                                primary={module.name}
                                                                primaryTypographyProps={{ 
                                                                    variant: 'body2',
                                                                    fontWeight: 500 
                                                                }}
                                                            />
                                                            <ArrowForward sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        </ListItemButton>
                                                    </ListItem>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </motion.div>

            {/* Footer Help Section */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
            >
                <Box sx={{ mt: 4, p: 3, bgcolor: theme.palette.grey[50], borderRadius: 3, textAlign: 'center' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="center" spacing={3}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                {userType === 'club' 
                                    ? "Need help with Club Management?" 
                                    : "Need help with Hotel Management?"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Contact your system administrator for assistance with any modules
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </motion.div>
        </Container>
    );
};

export default Dashboard;